import React, { useState } from "react";
import { Form, Input, Button, message } from "antd";
import sanitizeHtml from "sanitize-html";
import ReactHtmlParser from "react-html-parser";
import { useAuth } from "../../../authentication";
import { validatePassword } from "../../../constants/Validation";
import UsersAPI from "../../../services/Users/Users";
import "./change-password.css";

const usersApi = new UsersAPI();

const ChangePassword = () => {
  const [changePasswordForm] = Form.useForm();
  const { authUser } = useAuth();
  const [isBtnLoading, setBtnLoading] = useState(false);

  const handleSubmit = async (values) => {
    try {
      setBtnLoading(true);

      let tempCurrentPassword = ReactHtmlParser(
        sanitizeHtml(values.currentPassword),
      );
      let tempNewPassword = ReactHtmlParser(sanitizeHtml(values.newPassword));
      let tempNewConfirmPassword = ReactHtmlParser(
        sanitizeHtml(values.newConfirmPassword),
      );
      if (
        tempCurrentPassword[0] !== "" &&
        tempNewPassword[0] !== "" &&
        tempNewConfirmPassword[0] !== ""
      ) {
        const payload = {
          currentPassword: tempCurrentPassword[0],
          newPassword: tempNewPassword[0],
          newConfirmPassword: tempNewConfirmPassword[0],
          userId: authUser.id,
          role: authUser.role,
        };
        const response = await usersApi.changePassword(payload);
        if (response && response.data) {
          if (response.data.isChanged) {
            message.success("Your password has been updated successfully.");
            changePasswordForm.resetFields();
          } else {
            message.error(response.data.message);
          }
        } else {
          message.error("Something went wrong, please try again later.");
        }
      } else {
        message.error("Invalid data. Please put valid data in the fields.");
      }
    } catch (error) {
      if (error.response.status === 429) {
        message.error(error.response.data);
      } else {
        message.error("Something went wrong, please try again later.");
      }
    } finally {
      setBtnLoading(false);
    }
  };

  // Custom input event handler to prevent spacebar key press
  const handleInput = (e) => {
    if (e.key === " ") {
      e.preventDefault();
    }
  };

  return (
    <Form
      form={changePasswordForm}
      onFinish={handleSubmit}
      layout="vertical"
      requiredMark={false}
    >
      <Form.Item
        className="custom-form-item"
        label={
          <span className="custom-form-item">
            Current Password : <span style={{ color: "red" }}>*</span>
          </span>
        }
        name="currentPassword"
        rules={[
          {
            required: true,
            whitespace: true,
            message: (
              <span className="custom-error-message">
                Please enter current password.
              </span>
            ),
          },
          {
            validator: (_, value) => {
              if (/\s/.test(value)) {
                // If the value contains spaces, reject with an error message
                return Promise.reject(
                  <span className="custom-error-message">
                    Password cannot contain spaces.
                  </span>,
                );
              }
              return Promise.resolve();
            },
          },
        ]}
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
      >
        <Input.Password
          className="custom-input"
          placeholder="Enter current password"
          onKeyDown={handleInput}
        />
      </Form.Item>

      <Form.Item
        className="custom-form-item"
        label={
          <span>
            New Password : <span style={{ color: "red" }}>*</span>
          </span>
        }
        name="newPassword"
        hasFeedback
        rules={[
          {
            required: true,
            whitespace: true,
            message: (
              <span className="custom-error-message">
                Please enter new password.
              </span>
            ),
          },
          {
            validator: validatePassword,
          },
        ]}
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
      >
        <Input.Password
          className="custom-input"
          placeholder="Enter new password"
          onKeyDown={handleInput}
        />
      </Form.Item>

      <Form.Item
        className="custom-form-item"
        label={
          <span>
            Confirm Password : <span style={{ color: "red" }}>*</span>
          </span>
        }
        name="newConfirmPassword"
        dependencies={["newPassword"]}
        hasFeedback
        rules={[
          {
            required: true,
            whitespace: true,
            message: (
              <span className="custom-error-message">
                Please confirm new password.
              </span>
            ),
          },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue("newPassword") === value) {
                return Promise.resolve();
              }
              if(value.length >= 8) {
                return Promise.reject(
                  <span className="custom-error-message">
                    New password and confirm new password field do not match.
                  </span>,
                );
              }
            },
          }),
        ]}
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
      >
        <Input.Password
          className="custom-input"
          placeholder="Confirm new password"
          onKeyDown={handleInput}
        />
      </Form.Item>

      <Form.Item
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
        style={{ textAlign: "center", paddingTop: "20px" }}
      >
        <Button
          className="custom-button"
          type="primary"
          htmlType="submit"
          loading={isBtnLoading}
        >
          Change Password
        </Button>
      </Form.Item>
    </Form>
  );
};

export default ChangePassword;
